import * as React from 'react';
import { Info, Stethoscope } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';
import { ICON_PX } from './lib/iconSizes';
import { cn } from './lib/utils';

/**
 * Canonical info-icon + tooltip used inside SectionHeading and elsewhere.
 *
 * Every info tooltip automatically includes an "Ask NexoNurse Assistant"
 * link at the bottom. Clicking it opens the chatbot with the section title
 * as context — no per-instance `aiContext` prop needed.
 *
 * The `aiContext` prop can still be passed for explicit context overrides
 * (e.g. mapping to a specific feature ID). If omitted, the section `title`
 * is used as the chatbot context.
 */
function SectionInfoIcon({ children, aiContext, title }) {
  // Derive context: explicit aiContext > title-based context
  const chatContext = aiContext || title || 'general';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"
        >
          <Info size={ICON_PX.md} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" maxWidth={240}>
        <div>
          {children}
          <button
            type="button"
            className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-white/20
                       text-aqua-bright hover:text-white transition-colors w-full"
            onClick={(e) => {
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('nexonurse:open-chatbot', { detail: chatContext }));
            }}
          >
            <Stethoscope size={10} />
            <span>Ask NexoNurse Assistant</span>
          </button>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Page / section header with an optional right-side action slot.
 *
 * `info`  — string tooltip text; auto-renders the canonical info icon.
 *           Every info tooltip automatically includes an "Ask NexoNurse Assistant"
 *           link at the bottom — users can drill into the chatbot if the static
 *           tooltip text isn't enough. No per-instance config needed.
 * `aiContext` — optional explicit chatbot context override (e.g. "staffing.coverage-status").
 *              If omitted, the section `title` is used as context for the chatbot.
 * `action` — custom JSX for the right slot (settings gear, extra buttons, etc.).
 *            When both `info` and `action` are provided they render side-by-side.
 * `serif` — uses Source Serif 4 for the title.
 * `size`  — "sm" | "md" (default "md")
 */
const SIZE_CLASS = {
  sm: 'text-heading-sm',
  md: 'text-heading-md',
};

function SectionHeading({ title, subtitle, info, aiContext, action, serif = false, size = 'md', className, ...props }) {
  const infoIcon = info ? <SectionInfoIcon aiContext={aiContext} title={title}>{info}</SectionInfoIcon> : null;

  const rightSlot = (infoIcon && action)
    ? <div className="flex items-center gap-1.5">{action}{infoIcon}</div>
    : infoIcon || action || null;

  return (
    <div className={cn('flex items-center justify-between gap-4', className)} {...props}>
      <div className="min-w-0">
        <h2
          className={cn(
            SIZE_CLASS[size] ?? SIZE_CLASS.md,
            'text-text-dark leading-tight',
            serif ? 'font-heading' : 'font-body font-bold'
          )}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-body-sm text-text-dark-secondary mt-0.5">{subtitle}</p>
        )}
      </div>
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </div>
  );
}

export { SectionHeading, SectionInfoIcon };
